import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { ContextService } from 'app/core/services/context.service';
import { MatErrorMessagesDirective } from 'app/shared/directives';
import { TrailerStatus, TrailerType } from 'app/shared/enums';
import { SitesService } from 'app/shared/services';
import { BehaviorSubject, combineLatest, map, mergeMap, of, startWith, tap } from 'rxjs';

@Component({
  selector: 'app-trailer-statuses',
  imports: [
    MatChipsModule,
    TranslateModule,
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatErrorMessagesDirective,
  ],
  templateUrl: './trailer-statuses.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrailerStatusesComponent {
  @Input() public controlName!: string;
  @Input() public form!: FormGroup;
  @Input() public disabled = false;

  private sitesService = inject(SitesService);
  private contextService = inject(ContextService);
  private destroyRef = inject(DestroyRef);

  private trailerTypeSubject = new BehaviorSubject<TrailerType | string | null | undefined>(null);
  private trailerTypeInputSet = false;

  @Input()
  public set trailerType(value: TrailerType | string | null | undefined) {
    this.trailerTypeSubject.next(value);
    this.trailerTypeInputSet = true;
  }

  public trailerStatuses$ = combineLatest([
    this.contextService.siteId,
    this.trailerTypeSubject.asObservable().pipe(startWith(null)),
  ]).pipe(
    mergeMap(([siteId]) => (siteId ? this.sitesService.getTrailerStatuses(siteId) : of(null))),
    map((res) => res?.value?.trailerStatusesResponse),
    tap(() => {
      // Reset control value if trailerType changes and the selected status is PRE_COOLING
      const currentValue = this.inputControl?.value;
      if (this.trailerTypeSubject.value && currentValue === TrailerStatus.PRE_COOLING && !this.isReefer) {
        this.inputControl?.reset();
        this.inputControl?.updateValueAndValidity();
      }
    }),
    map((trailerStatuses) =>
      trailerStatuses?.filter((status) =>
        this.isReefer || !this.trailerTypeInputSet ? true : status.code !== TrailerStatus.PRE_COOLING,
      ),
    ),
    takeUntilDestroyed(this.destroyRef),
  );

  public get inputControl(): AbstractControl<string> | null {
    return this.form.get(this.controlName);
  }

  private get isReefer(): boolean {
    return (
      this.trailerTypeSubject.value === TrailerType.CONTAINER_REEFER ||
      this.trailerTypeSubject.value === TrailerType.REEFER
    );
  }
}
